import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore, hasPermission } from '@/lib/auth';

/**
 * Wraps any /admin route — bounces anyone with no business in the shell.
 *
 *   - No session → /login with return URL
 *   - Authed but holds none of the admin-panel permissions → /account
 *   - Authed with at least one → render children
 *
 * Anyone holding at least one admin-panel permission gets in. What they
 * then see is decided per nav item in AdminShell — a supervisor reaching
 * /admin/items still hits the backend's own requirePermission and gets a
 * 403, so this guard only decides "does the shell open at all".
 *
 * Deliberately not isStaff(): a driver is staff but has no business in the
 * admin panel — they get their own app. Gating on held permissions rather
 * than not-a-customer is the correct line.
 */
const ADMIN_PANEL_PERMISSIONS = [
  'orders.view',
  'catalog.view',
  'drivers.view',
  'dispatch.view',
  'customers.view',
  'coupons.view',
  'cashfloat.view',
  'reports.operational.view',
  'settings.operational.edit',
];

export function AdminGuard({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isAuthed = useAuthStore((s) => s.isAuthenticated());
  const loc = useLocation();

  if (!isAuthed || !user) {
    return <Navigate to={`/login?return=${encodeURIComponent(loc.pathname)}`} replace />;
  }
  if (!ADMIN_PANEL_PERMISSIONS.some((p) => hasPermission(user, p))) {
    return <Navigate to="/account" replace />;
  }
  return <>{children}</>;
}
